import React, { Dispatch, SetStateAction, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

export const NoxusSortable = ({
  items,
  setItems,
  SortableItem = BaseSortableItem,
  isDisabled = false,
  className,
}: {
  items: any[];
  setItems: Dispatch<SetStateAction<any[]>>;
  SortableItem?: any;
  isDisabled?: boolean;
  className?: string;
}) => {
  const [isAnyDragging, setIsAnyDragging] = useState(false);

  //filter undefined items
  const filteredItems = items.filter((item) => item !== undefined);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    setIsAnyDragging(false);
    const { active, over } = event;

    if (active?.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item?.id === active?.id);
        const newIndex = items.findIndex((item) => item?.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <div
      className={cn(
        "w-full flex flex-col gap-3",
        isAnyDragging && "[&_*]:!cursor-grabbing",
        className
      )}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsAnyDragging(true)}
        onDragCancel={() => setIsAnyDragging(false)}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={filteredItems}
          strategy={verticalListSortingStrategy}
          disabled={isDisabled}
        >
          {filteredItems.map((item, index) => (
            <SortableItem
              key={item?.id}
              id={item?.id}
              index={index}
              item={item}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

const BaseSortableItem = ({
  id,
  index,
  item,
  hasNumber,
  isDisabled = false,
}: {
  id: string;
  index: number;
  item: string;
  hasNumber?: boolean;
  isDisabled?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className="flex items-center justify-between w-full"
      ref={setNodeRef}
      style={style}
    >
      {hasNumber && (
        <p className="text-primary text-xs mr-4 min-w-5">{`${index + 1}.`}</p>
      )}
      <p className="flex-1 text-primary mr-6">{item}</p>
      <button
        type="button"
        {...attributes}
        {...listeners}
        color="gray.300"
        disabled={isDisabled}
        aria-disabled={isDisabled}
      >
        <GripVertical size="1rem" strokeWidth="1.25px" />
      </button>
    </div>
  );
};
